/**
 * Security monitoring (superadmin only).
 *
 * Reads authMonitoringService, an in-memory log kept by this browser tab: the
 * sign-in, password-reset and session events that happened here since the tab
 * was opened. It is not platform data, and the page says so.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import LoginOutlinedIcon from '@mui/icons-material/LoginOutlined';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import AutorenewOutlinedIcon from '@mui/icons-material/AutorenewOutlined';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import { authMonitoringService } from '../../services/authMonitoringService';
import { formatDateTime, formatRelativeTime, pluralize } from '../../utils/format';
import PageContainer from '../ui/PageContainer';
import PageHeader from '../ui/PageHeader';
import Section from '../ui/Section';
import StatCard from '../ui/StatCard';
import StatusChip from '../ui/StatusChip';
import EmptyState from '../ui/EmptyState';
import KeyValueList from '../ui/KeyValueList';
import {
  buildReasonRows,
  describeAuthError,
  describeContext,
  formatMetadataValue,
  getAuthEventMeta,
  humanize,
  newestFirst,
  RecordedAuthEvent,
} from './securityMonitoringModel';

const REFRESH_INTERVAL_MS = 10000;
const EVENT_LIMIT = 100;

type Metrics = ReturnType<typeof authMonitoringService.getMetrics>;
type Suspicious = ReturnType<typeof authMonitoringService.detectSuspiciousActivity>;

interface Snapshot {
  metrics: Metrics;
  events: RecordedAuthEvent[];
  suspicious: Suspicious;
  exported: Record<string, number | string>;
}

const takeSnapshot = (): Snapshot => ({
  metrics: authMonitoringService.getMetrics(),
  events: newestFirst(authMonitoringService.getRecentEvents(EVENT_LIMIT) as RecordedAuthEvent[]),
  suspicious: authMonitoringService.detectSuspiciousActivity(),
  exported: authMonitoringService.exportMetrics(),
});

const When: React.FC<{ timestamp: number }> = ({ timestamp }) => (
  <Typography component="time" variant="body2" dateTime={new Date(timestamp).toISOString()} title={formatDateTime(timestamp)} sx={{ whiteSpace: 'nowrap' }}>
    {formatRelativeTime(timestamp)}
  </Typography>
);

const AccountId: React.FC<{ value?: string }> = ({ value }) =>
  value ? (
    <Typography component="span" variant="body2" sx={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: '0.8125rem' }}>
      {value}
    </Typography>
  ) : (
    <Typography component="span" variant="body2" color="text.secondary">
      —
    </Typography>
  );

const SecurityMonitoring: React.FC = () => {
  const theme = useTheme();
  const compact = useMediaQuery(theme.breakpoints.down('md'));
  const [snapshot, setSnapshot] = useState<Snapshot>(takeSnapshot);
  const [selectedEvent, setSelectedEvent] = useState<RecordedAuthEvent | null>(null);
  const [toast, setToast] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const refresh = useCallback(() => setSnapshot(takeSnapshot()), []);

  useEffect(() => {
    const interval = setInterval(refresh, REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [refresh]);

  const { metrics, events, suspicious, exported } = snapshot;
  const hasData = metrics.eventsCount > 0;
  const reasons = useMemo(() => buildReasonRows(metrics.errorBreakdown, metrics.failedLogins), [metrics]);
  const show = (value: number) => (hasData ? value : '—');

  const copyCounters = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(exported, null, 2));
      setToast({ open: true, message: 'Counters copied', severity: 'success' });
    } catch (error) {
      console.warn('Could not copy counters:', error);
      setToast({ open: true, message: "Couldn't copy: this browser blocked the clipboard.", severity: 'error' });
    }
  };

  const detailsButton = (event: RecordedAuthEvent) => (
    <Button size="small" onClick={() => setSelectedEvent(event)} aria-label={`Details for ${getAuthEventMeta(event).label.toLowerCase()} ${formatRelativeTime(event.timestamp)}`}>
      Details
    </Button>
  );

  const renderEvents = () => {
    if (events.length === 0) {
      return (
        <EmptyState
          compact
          icon={<LoginOutlinedIcon />}
          title="No events in this tab yet"
          description="Sign-ins, failed attempts and session refreshes appear here as they happen in this tab."
        />
      );
    }
    if (compact) {
      return (
        <Box component="ul" aria-label="Recent events" sx={{ listStyle: 'none', m: 0, p: 0 }}>
          {events.map((event, index) => {
            const meta = getAuthEventMeta(event);
            return (
              <Box
                component="li"
                key={`${event.timestamp}-${index}`}
                sx={{ px: 2.5, py: 1.75, borderTop: index === 0 ? 0 : 1, borderColor: 'divider' }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5 }}>
                  <StatusChip label={meta.label} tone={meta.tone} />
                  <When timestamp={event.timestamp} />
                </Box>
                <Box
                  component="dl"
                  sx={{ m: 0, mt: 1.25, display: 'grid', gridTemplateColumns: 'auto minmax(0, 1fr)', columnGap: 2, rowGap: 0.5 }}
                >
                  <Typography component="dt" variant="body2" color="text.secondary">Account</Typography>
                  <Box component="dd" sx={{ m: 0, minWidth: 0, overflowWrap: 'anywhere' }}><AccountId value={event.userIdentifier} /></Box>
                  <Typography component="dt" variant="body2" color="text.secondary">Where</Typography>
                  <Typography component="dd" variant="body2" sx={{ m: 0 }}>{describeContext(event.context) || '—'}</Typography>
                  {event.errorCode && (
                    <>
                      <Typography component="dt" variant="body2" color="text.secondary">Reason</Typography>
                      <Typography component="dd" variant="body2" sx={{ m: 0 }}>{describeAuthError(event.errorCode)}</Typography>
                    </>
                  )}
                </Box>
                <Box sx={{ mt: 0.5, ml: -1 }}>{detailsButton(event)}</Box>
              </Box>
            );
          })}
        </Box>
      );
    }
    return (
      <Table aria-label="Recent events" size="small">
        <TableHead>
          <TableRow>
            <TableCell>When</TableCell>
            <TableCell>Event</TableCell>
            <TableCell>Account (hashed)</TableCell>
            <TableCell>Where</TableCell>
            <TableCell>Reason</TableCell>
            <TableCell align="right">
              <Box component="span" sx={{ position: 'absolute', width: '1px', height: '1px', overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>
                Actions
              </Box>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {events.map((event, index) => {
            const meta = getAuthEventMeta(event);
            return (
              <TableRow key={`${event.timestamp}-${index}`} sx={{ '&:last-of-type td': { borderBottom: 0 } }}>
                <TableCell><When timestamp={event.timestamp} /></TableCell>
                <TableCell><StatusChip label={meta.label} tone={meta.tone} /></TableCell>
                <TableCell><AccountId value={event.userIdentifier} /></TableCell>
                <TableCell>{describeContext(event.context) || '—'}</TableCell>
                <TableCell>{event.errorCode ? describeAuthError(event.errorCode) : '—'}</TableCell>
                <TableCell align="right">{detailsButton(event)}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    );
  };

  const selectedMeta = selectedEvent ? getAuthEventMeta(selectedEvent) : null;

  return (
    <PageContainer>
      <PageHeader
        eyebrow="Superadmin"
        title="Security monitoring"
        subtitle="Sign-in and session events recorded in this browser tab. Only superadmins can open this page."
        meta={<span>Updates every 10 seconds</span>}
        action={
          <Button variant="outlined" startIcon={<RefreshOutlinedIcon />} onClick={refresh}>
            Refresh
          </Button>
        }
      />

      {/* A standing note, not news: role="note" keeps screen readers from announcing it as an alert. */}
      <Alert severity="info" role="note" sx={{ mb: 3 }}>
        <AlertTitle>This page shows this browser tab only</AlertTitle>
        These numbers come from this tab since it was opened, and reset when it reloads. They don&apos;t include other
        people, other tabs or other devices: PithPlay doesn&apos;t collect sign-in events across the platform yet.
      </Alert>

      {suspicious.hasSuspiciousActivity && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <AlertTitle>Unusual activity in this tab</AlertTitle>
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            {suspicious.alerts.map((alert) => (
              <li key={alert}>
                <Typography variant="body2">{alert}</Typography>
              </li>
            ))}
          </Box>
          {suspicious.recommendations.length > 0 && (
            <>
              <Typography variant="body2" sx={{ fontWeight: 600, mt: 1.5 }}>
                What to check
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                {suspicious.recommendations.map((recommendation) => (
                  <li key={recommendation}>
                    <Typography variant="body2">{recommendation}</Typography>
                  </li>
                ))}
              </Box>
            </>
          )}
        </Alert>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 1.5 }}>
        <Typography component="h2" sx={{ fontWeight: 700, fontSize: '1rem' }}>
          Events recorded in this browser tab
        </Typography>
        <Button size="small" startIcon={<ContentCopyOutlinedIcon />} onClick={copyCounters}>
          Copy counters
        </Button>
      </Box>
      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<LoginOutlinedIcon />}
            label="Sign-in attempts"
            value={show(metrics.totalLoginAttempts)}
            caption={hasData && metrics.totalLoginAttempts > 0 ? `${metrics.successfulLogins} succeeded` : undefined}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<ErrorOutlineOutlinedIcon />}
            label="Failed attempts"
            value={show(metrics.failedLogins)}
            caption={hasData ? 'Sign-in and password reset' : undefined}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<BlockOutlinedIcon />}
            label="Rate-limit blocks"
            value={show(metrics.rateLimitTriggers)}
            caption={hasData ? 'On the sign-in form' : undefined}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<AutorenewOutlinedIcon />}
            label="Session refreshes"
            value={show(metrics.tokenRefreshes)}
            caption={hasData ? `${pluralize(metrics.sessionsExpired, 'session')} expired` : undefined}
          />
        </Grid>
      </Grid>

      <Box sx={{ display: 'grid', gap: 3 }}>
        <Section title="Failed attempts by reason" disablePadding>
          {reasons.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ px: 2.5, py: 2 }}>
              {hasData ? 'No failed attempts in this tab.' : 'Nothing recorded in this tab yet.'}
            </Typography>
          ) : (
            <Box component="ul" aria-label="Failed attempts by reason" sx={{ listStyle: 'none', m: 0, p: 0 }}>
              {reasons.map((row, index) => (
                <Box
                  component="li"
                  key={row.code}
                  sx={{
                    display: 'flex',
                    alignItems: 'baseline',
                    justifyContent: 'space-between',
                    gap: 2,
                    px: 2.5,
                    py: 1.5,
                    borderTop: index === 0 ? 0 : 1,
                    borderColor: 'divider',
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {row.label}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                    <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
                      {row.count}
                    </Box>
                    {` · ${row.share}%`}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </Section>

        <Section
          title="Recent events"
          description={`The last ${EVENT_LIMIT} events in this tab, newest first.`}
          disablePadding
        >
          {renderEvents()}
        </Section>

      </Box>

      <Dialog open={Boolean(selectedEvent)} onClose={() => setSelectedEvent(null)} maxWidth="sm" fullWidth aria-labelledby="event-details-title">
        <DialogTitle id="event-details-title">{selectedMeta?.label ?? 'Event'}</DialogTitle>
        <DialogContent>
          {selectedEvent && (
            <KeyValueList
              items={[
                { label: 'When', value: formatDateTime(selectedEvent.timestamp) },
                { label: 'Account (hashed)', value: selectedEvent.userIdentifier },
                { label: 'Where', value: describeContext(selectedEvent.context) },
                {
                  label: 'Reason',
                  value: selectedEvent.errorCode ? `${describeAuthError(selectedEvent.errorCode)} (${selectedEvent.errorCode})` : '',
                },
                ...Object.entries(selectedEvent.metadata || {}).map(([key, value]) => ({
                  label: humanize(key),
                  value: formatMetadataValue(value),
                  fullWidth: key === 'errorMessage',
                })),
              ]}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setSelectedEvent(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setToast((prev) => ({ ...prev, open: false }))} severity={toast.severity} variant="filled">
          {toast.message}
        </Alert>
      </Snackbar>
    </PageContainer>
  );
};

export default SecurityMonitoring;
